class Accordion extends HTMLElement {
  /**
   * Initializes the Accordion component.
   */
  constructor() {
    super();

    this.ACTIVE_CLASS = "accordion__item--open";
  }

  /**
   * Lifecycle method called when the element is added to the DOM.
   */
  connectedCallback() {
    if ((this.mobileOnly || this.touchOnly) && !this.mediaQuery.matches) return;

    this.addListeners();
  }

  /**
   * Adds necessary event listeners.
   */
  addListeners() {
    this.addButtonListeners();

    subscribe(PUB_SUB_EVENTS.toggleAccordion, ({ item, button }) =>
      this.toggleStates(item, button));
  }

  /**
   * Adds click event listeners to accordion buttons and opens first item if needed.
   */
  addButtonListeners() {
    this.accordionButtons.forEach((button) => {
      const accordionItem = button.closest("[data-accordion-item]");
      const openFirstItem = accordionItem.getAttribute("data-open-first") === "true";

      button.addEventListener("click", () => this.toggleStates(accordionItem, button));

      if (openFirstItem === true) this.openAccordion(accordionItem, button);
    });
  }

  /**
   * Toggles active states of clicked accordion.
   * @param {Element} item - The accordion item element.
   * @param {Element} button - The accordion button element.
   */
  toggleStates(item, button) {
    button.getAttribute("aria-expanded") === "false"
      ? this.openAccordionEvent(item, button)
      : this.closeAccordionEvent(item, button);
  }

  /**
   * Handles opening an accordion item, with auto-close delay if needed.
   * @param {Element} item - The accordion item element.
   * @param {Element} button - The accordion button element.
   */
  openAccordionEvent(item, button) {
    let isDelay = false;

    if (this.autoClose) isDelay = this.closeAccordions();

    setTimeout(() => this.openAccordion(item, button), isDelay ? 250 : 0);
  }

  /**
   * Handles closing an accordion item and publishes an event.
   * @param {Element} item - The accordion item element.
   * @param {Element} button - The accordion button element.
   */
  closeAccordionEvent(item, button) {
    this.closeAccordion(item, button);

    publish(PUB_SUB_EVENTS.accordionClosed, { element: item });
  }

  /**
   * Closes all open accordion items if auto-close is enabled.
   * @returns {boolean} Whether a delay is needed for closing.
   */
  closeAccordions() {
    let isDelay = false;

    this.accordionItems.forEach(item => {
      const button = item.querySelector("[data-accordion-button]");

      if (button.getAttribute("aria-expanded") === "true") {
        this.closeAccordion(item, button);

        isDelay = true;
      }
    });

    return isDelay;
  }

  /**
   * Opens an accordion item and animates its content.
   * @param {Element} item - The accordion item element.
   * @param {Element} button - The accordion button element.
   */
  openAccordion(item, button) {
    item.classList.add(this.ACTIVE_CLASS);
    button.setAttribute("aria-expanded", "true");

    const content = item.querySelector("[data-accordion-content]");

    // Set properties to calculate height whilst hiding element
    content.style.width = getComputedStyle(content).width;
    content.style.position = "absolute";
    content.style.visibility = "hidden";
    content.style.height = "auto";

    const height = getComputedStyle(content).height;

    // Reset properties used above to calculate height
    content.style.width = null;
    content.style.position = null;
    content.style.visibility = null;
    content.style.height = "0";

    // Force repaint
    getComputedStyle(content).height;
    requestAnimationFrame(() => (content.style.height = height));

    content.addEventListener("transitionend", () => {
      if (item.getAttribute("aria-expanded") === "true")
        content.style.height = "auto";
    });
  }

  /**
   * Closes an accordion item and animates its content.
   * @param {Element} item - The accordion item element.
   * @param {Element} button - The accordion button element.
   */
  closeAccordion(item, button) {
    item.classList.remove(this.ACTIVE_CLASS);
    button.setAttribute("aria-expanded", "false");

    const content = item.querySelector("[data-accordion-content]");

    content.style.height = getComputedStyle(content).height;

    getComputedStyle(content).height;
    requestAnimationFrame(() => (content.style.height = "0"));
  }

  /**
   * Returns all accordion button elements.
   * @returns {NodeList} A list of accordion button elements.
   */
  get accordionButtons() {
    return this.querySelectorAll("[data-accordion-button]");
  }

  /**
   * Returns all accordion item elements.
   * @returns {NodeList} A list of accordion item elements.
   */
  get accordionItems() {
    return this.querySelectorAll("[data-accordion-item]");
  }

  /**
   * Returns whether auto-close is enabled.
   * @returns {boolean} True if auto-close is enabled, false otherwise.
   */
  get autoClose() {
    return this.getAttribute("data-auto-close") === "true";
  }

  /**
   * Returns whether accordion is mobile only.
   * @returns {boolean} True if mobile only, false otherwise.
   */
  get mobileOnly() {
    return this.getAttribute("data-touch-only") === "true";
  }

  /**
   * Returns whether accordion is touch only.
   * @returns {boolean} True if touch only, false otherwise.
   */
  get touchOnly() {
    return this.getAttribute("data-touch-only") === "true";
  }

  /**
   * Returns the media query for mobile.
   * @returns {MediaQueryList} The media query list object.
   */
  get mediaQuery() {
    return window.matchMedia("(max-width: 1249px)");
  }
}

if (!customElements.get("accordion-wrapper"))
  customElements.define("accordion-wrapper", Accordion);
